import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { cn } from '../../lib/cn'
import { imNachlauf, useGelesenSetzen, useNachlaufTakt, type FeedEintrag } from '../../lib/gelesen'
import { aktivitaetsfeedQuery } from '../../lib/queries'
import { FeedFenster } from '../Aktivitaetsfeed'
import { AufgabenVerweis } from '../AufgabeKontext'
import { Button } from '../ui/primitives'

/** Wie viele Aufgaben je Gruppe offen dastehen. Der Rest steht im Fenster dahinter. */
export const JE_GRUPPE = 4

/**
 * **Eine Aufgabe in der Nachrichtenspalte.** Gruppiert wird je Aufgabe und nicht je Ereignis –
 * vier Kommentare an einem Ticket sind eine Nachricht, nicht vier
 * ([E-198](../../../../../docs/ENTSCHEIDUNGEN.md)).
 */
export type Nachricht = {
  taskId: string
  titel: string
  /**
   * **Was in der Zeile steht.** Bei einer Erwähnung die Erwähnung – auch wenn danach noch
   * kommentiert wurde: sie ist der Grund, warum die Aufgabe hier oben liegt. Sonst das Jüngste.
   */
  zeigen: FeedEintrag
  /**
   * **Bis wohin ein Haken gilt** – der Zeitpunkt des **jüngsten** Kommentars dieser Aufgabe,
   * nicht der der gezeigten Zeile. Sonst bliebe bei einer Erwähnung mit späterem Kommentar nach
   * dem Abhaken ein Rest ungelesen, und die Aufgabe spränge in die Gruppe darunter.
   */
  bis: string
  gelesen: boolean
  /** Wie viele weitere Kommentare derselben Aufgabe darunter lägen. */
  weitere: number
}

export type Nachrichtenlage = {
  /** Jemand hat mich beim Namen genannt. Ein Mensch wartet. */
  erwaehnt: Nachricht[]
  /** Ungelesene Kommentare ohne Ansprache – eine Mitteilung. */
  antworten: Nachricht[]
  /** Gerade abgehakt und noch im Nachlauf, damit der Haken zurückgenommen werden kann. */
  gelesen: Nachricht[]
  /** Alles, was kein Kommentar ist: Statuswechsel, Zuweisungen, Label. Nur gezählt. */
  sonst: { aufgaben: number; ereignisse: number }
}

/**
 * **Zone 4 des Modus „Einfach" – alles, was jemand geschrieben hat, an einer Stelle**
 * ([Z-12](../../../../../docs/phasen/zusatz-12-mein-tag-einfach.md)).
 *
 * Nils, 28.08.2026: *„Plus eine Zone/Gruppe wo dann gut übersichtlich und gruppiert die
 * Kommentarsachen (erwähnt, gelesen, feed, usw.) liegen."*
 *
 * ## Warum das eine eigene Spalte ist
 *
 * Die drei Zeit-Zonen sind eine Achse: Vorlauf → Jetzt → Erledigt. Eine Nachricht hat auf dieser
 * Achse **keinen Platz** – sie kommt quer herein, egal woran man gerade arbeitet. Deshalb steht
 * sie daneben und läuft beim Blättern mit, statt sich zwischen zwei Zonen zu drängen.
 *
 * ## Warum sie „Klopft an" die Kommentare abnimmt
 *
 * Vorher standen Erwähnungen und Antworten **oben** in der roten Zone und zusätzlich unten im
 * Feed. Zwei Orte für dieselbe Nachricht heißen: an einem davon steht sie noch, wenn man sie am
 * anderen erledigt hat. Seit [E-270](../../../../../docs/ENTSCHEIDUNGEN.md#e-270) trägt „Klopft
 * an" im Modus „Einfach" nur noch, was **keine** Nachricht ist (Frist, Abnahme) – Kommentare
 * liegen hier, in vier Gruppen mit Zahl.
 *
 * ## Der Haken ist derselbe wie überall
 *
 * `useGelesenSetzen`, `feed_gelesen`, zehn Minuten Nachlauf
 * ([E-203](../../../../../docs/ENTSCHEIDUNGEN.md), [E-229](../../../../../docs/ENTSCHEIDUNGEN.md#e-229)).
 * Wer hier abhakt, findet es im Aktivitätsfeed abgehakt – es ist ein Serverstand, keine Ansicht.
 */
export function Nachrichten() {
  const { data: feed = [] } = useQuery(aktivitaetsfeedQuery(14))
  const jetzt = useNachlaufTakt(feed)
  const [fensterOffen, setFensterOffen] = useState(false)

  const lage = nachrichtenGruppen(feed, jetzt)
  const offen = lage.erwaehnt.length + lage.antworten.length

  return (
    <>
      <section className="overflow-hidden rounded-card border border-line bg-surface shadow-card">
        <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-line px-[15px] py-3">
          <h3 className="text-[12.5px] font-bold">Nachrichten</h3>
          <span className="text-[11.5px] text-muted">
            {offen === 0 ? 'nichts Ungelesenes' : `${offen} offen`}
          </span>
        </header>

        <Gruppe
          titel="Dich erwähnt"
          nachrichten={lage.erwaehnt}
          laut
          mehrZeigen={() => setFensterOffen(true)}
        />
        <Gruppe
          titel="Antworten bei dir"
          nachrichten={lage.antworten}
          mehrZeigen={() => setFensterOffen(true)}
        />
        <Gruppe
          titel="Gelesen"
          nachrichten={lage.gelesen}
          blass
          mehrZeigen={() => setFensterOffen(true)}
        />

        {/**
         * **Der Rest wird gezählt, nicht ausgebreitet.** Ein Statuswechsel ist eine Auskunft,
         * keine Nachricht: dass er passiert ist, will man wissen – jede einzelne Zeile davon
         * nicht. Wer doch hinsehen will, bekommt denselben Feed wie überall.
         */}
        <div className="flex flex-wrap items-center gap-2 border-t border-line px-[15px] py-2.5">
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] font-bold tracking-[0.07em] text-muted uppercase">
              Sonst passiert
            </div>
            <div className="mt-0.5 text-[11.5px] text-muted">
              {lage.sonst.ereignisse === 0
                ? 'nichts in den letzten 14 Tagen'
                : `${lage.sonst.ereignisse} ${
                    lage.sonst.ereignisse === 1 ? 'Ereignis' : 'Ereignisse'
                  } auf ${lage.sonst.aufgaben} ${
                    lage.sonst.aufgaben === 1 ? 'Aufgabe' : 'Aufgaben'
                  } · 14 Tage`}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setFensterOffen(true)}>
            Ansehen
          </Button>
        </div>
      </section>

      {fensterOffen && <FeedFenster schliessen={() => setFensterOffen(false)} />}
    </>
  )
}

/* ── Eine Gruppe ────────────────────────────────────────────────────── */

function Gruppe({
  titel,
  nachrichten,
  laut = false,
  blass = false,
  mehrZeigen,
}: {
  titel: string
  nachrichten: readonly Nachricht[]
  /** Die Erwähnung – rot, weil dort ein Mensch wartet. */
  laut?: boolean
  /** Die abgehakten im Nachlauf. */
  blass?: boolean
  mehrZeigen: () => void
}) {
  /**
   * **Eine leere Gruppe steht nicht da.** Vier Überschriften mit „0" untereinander sind eine
   * Wand, hinter der man die eine Zeile nicht mehr sieht, die zählt – dieselbe Regel wie bei
   * „Klopft an".
   */
  if (nachrichten.length === 0) return null

  const sichtbar = nachrichten.slice(0, JE_GRUPPE)
  const rest = nachrichten.length - sichtbar.length

  return (
    <div className="border-t border-line first:border-t-0">
      <div
        className={cn(
          'flex items-center gap-2 px-[15px] pt-2.5 pb-1 text-[10.5px] font-bold tracking-[0.07em] uppercase',
          laut ? 'text-pool-sofort' : 'text-muted',
        )}
      >
        {titel}
        <span
          className={cn(
            'tabular rounded-full px-[7px] py-[1px] text-[10px] font-bold tracking-normal',
            laut ? 'bg-pool-sofort-bg text-pool-sofort' : 'bg-[#f3f2ef] text-ink2',
          )}
        >
          {nachrichten.length}
        </span>
      </div>

      {sichtbar.map((n) => (
        <Zeile key={n.taskId} nachricht={n} blass={blass} />
      ))}

      {rest > 0 && (
        <button
          type="button"
          onClick={mehrZeigen}
          className="cursor-pointer px-[15px] pb-2 text-[11px] text-accent-deep hover:underline"
        >
          + {rest} {rest === 1 ? 'weitere Aufgabe' : 'weitere Aufgaben'} · alle ansehen
        </button>
      )}
    </div>
  )
}

/* ── Eine Zeile ─────────────────────────────────────────────────────── */

function Zeile({ nachricht: n, blass }: { nachricht: Nachricht; blass: boolean }) {
  const gelesenSetzen = useGelesenSetzen()

  return (
    <div
      className={cn(
        'flex items-start gap-2.5 px-[15px] py-2 not-first:border-t not-first:border-dashed not-first:border-line',
        blass && 'opacity-55',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] leading-snug">
          <b className="font-semibold">{n.zeigen.wer ?? 'jemand'}</b>{' '}
          <span className="text-ink2">
            {n.zeigen.erwaehntMich ? 'hat dich erwähnt' : 'hat geantwortet'}
          </span>
        </div>

        {/**
         * **Woher der Ruf kommt, wenn es nicht die Aufgabe selbst war** (22.09.2026,
         * [E-370](../../../../../docs/ENTSCHEIDUNGEN.md#e-370)).
         *
         * Christian Hunhoff hatte sich zum Ausprobieren in einer **Unteraufgabe** markiert und
         * fand danach nichts: *„Jedoch sehe ich diese erstmal nirgends."* Die Zeile stand hier –
         * sie sah nur aus wie jede andere, und in der geöffneten Aufgabe waren sechs von sieben
         * Unteraufgaben erledigt und eingeklappt.
         *
         * Deshalb der Titel und nicht bloß das Wort „Unteraufgabe": *dass* es eine ist, hilft
         * beim Suchen wenig – **welche**, beendet die Suche.
         */}
        {n.zeigen.unteraufgabe && (
          <div
            className="mt-[2px] truncate text-[11.5px] text-ink2"
            title={n.zeigen.unteraufgabe.titel}
          >
            <span className="text-muted">↳ Unteraufgabe</span> {n.zeigen.unteraufgabe.titel}
          </div>
        )}

        {/* Der Anfang des Kommentars – ohne ihn ist „Kommentar" keine Nachricht, sondern eine
            Aufforderung, die Aufgabe zu öffnen, um zu erfahren, ob sie eine war. */}
        {n.zeigen.auszug && (
          <div className="mt-[2px] truncate text-[12px] text-ink2" title={n.zeigen.auszug}>
            „{n.zeigen.auszug}"
          </div>
        )}

        <div className="mt-[3px] flex flex-wrap items-center gap-x-2 text-[11px] text-muted">
          <AufgabenVerweis
            aufgabeId={n.taskId}
            title={n.titel}
            className="max-w-full cursor-pointer truncate hover:text-accent hover:underline"
          >
            {n.titel}
          </AufgabenVerweis>
          <span>· {wann(n.zeigen.am)}</span>
          {n.weitere > 0 && <span>· + {n.weitere} weitere</span>}
        </div>
      </div>

      <button
        type="button"
        aria-pressed={n.gelesen}
        aria-label={`${n.gelesen ? 'Häkchen zurücknehmen' : 'Als gelesen abhaken'}: ${n.titel}`}
        title={
          n.gelesen
            ? 'Wieder als ungelesen führen'
            : 'Als gelesen abhaken – blendet sich in zehn Minuten aus'
        }
        onClick={() =>
          gelesenSetzen.mutate({
            taskId: n.taskId,
            /** Bis zum jüngsten Kommentar dieser Aufgabe – siehe `Nachricht.bis`. */
            bis: n.bis,
            gelesen: !n.gelesen,
          })
        }
        className={cn(
          'mt-0.5 flex size-[18px] shrink-0 cursor-pointer items-center justify-center rounded-[5px] border text-[10px] leading-none',
          n.gelesen
            ? 'border-accent bg-accent text-white'
            : 'border-line2 text-transparent hover:border-accent hover:text-accent-deep',
        )}
      >
        ✓
      </button>
    </div>
  )
}

/* ── Die Einteilung ─────────────────────────────────────────────────── */

/**
 * **Vier Töpfe aus einer Liste.**
 *
 * Der Server liefert absteigend nach Zeit; jede Gruppe behält diese Reihenfolge, weil eine `Map`
 * die des ersten Auftretens bewahrt. Die jüngste Zeile einer Aufgabe wird damit ihr Kopf.
 *
 * Eine Aufgabe steht in **höchstens einem** Topf: eine Erwähnung ist auch ein Kommentar, aber
 * sie zweimal zu zeigen hieße, dass die Zahl über „Antworten" etwas zählt, was daneben schon
 * lauter steht. Erwähnung schlägt Antwort, Ungelesenes schlägt Gelesenes.
 *
 * `jetzt` entscheidet, was noch im Nachlauf liegt – dieselbe Uhr wie im Feed (`useNachlaufTakt`),
 * damit eine abgehakte Zeile an beiden Stellen gleichzeitig geht.
 */
export function nachrichtenGruppen(feed: readonly FeedEintrag[], jetzt: number): Nachrichtenlage {
  /**
   * **Eingeteilt wird je Aufgabe, nicht je Ereignis.** Andernfalls landete eine Aufgabe mit
   * einer Erwähnung um 09:00 und einem Kommentar um 10:00 in zwei Gruppen gleichzeitig – und
   * beide Zahlen darüber wären für sich genommen richtig und zusammen gelogen.
   */
  const jeAufgabe = new Map<string, FeedEintrag[]>()
  for (const e of feed) {
    if (e.typ !== 'comment') continue
    const vorhanden = jeAufgabe.get(e.taskId)
    if (vorhanden) vorhanden.push(e)
    else jeAufgabe.set(e.taskId, [e])
  }

  const lage: Nachrichtenlage = {
    erwaehnt: [],
    antworten: [],
    gelesen: [],
    sonst: { aufgaben: 0, ereignisse: 0 },
  }

  for (const eintraege of jeAufgabe.values()) {
    /* Der Server liefert absteigend; die Gruppierung behält das, also ist [0] das Jüngste. */
    const neuester = eintraege[0] as FeedEintrag
    const ungelesen = eintraege.filter((e) => !e.gelesenAm)
    const erwaehnung = ungelesen.find((e) => e.erwaehntMich)

    /** Die Reihenfolge ist die Rangfolge: Ansprache vor Mitteilung, Offenes vor Abgehaktem. */
    const topf = erwaehnung
      ? lage.erwaehnt
      : ungelesen.length > 0
        ? lage.antworten
        : /** Abgehakt und länger als zehn Minuten her: hier fertig, im Feed weiterhin da. */
          imNachlauf(neuester.gelesenAm, jetzt)
          ? lage.gelesen
          : null
    if (!topf) continue

    const zeigen = erwaehnung ?? neuester
    topf.push({
      taskId: neuester.taskId,
      titel: neuester.titel,
      zeigen,
      bis: new Date(neuester.am).toISOString(),
      gelesen: !!neuester.gelesenAm,
      weitere: eintraege.length - 1,
    })
  }

  const uebrige = feed.filter((e) => e.typ !== 'comment' && !e.gelesenAm)
  lage.sonst = {
    aufgaben: new Set(uebrige.map((e) => e.taskId)).size,
    ereignisse: uebrige.length,
  }
  return lage
}

/** „09:41", „gestern", „Mi." – grob genug, dass es nicht bei jedem Blick springt. */
function wann(am: string | Date): string {
  const d = new Date(am)
  const heute = new Date()
  const tage = Math.floor(
    (Date.UTC(heute.getFullYear(), heute.getMonth(), heute.getDate()) -
      Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())) /
      86_400_000,
  )
  if (tage <= 0) return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  if (tage === 1) return 'gestern'
  if (tage < 7) return d.toLocaleDateString('de-DE', { weekday: 'short' })
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
}
